import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, LifeBuoy, Send } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { EmptyState } from '@/components/common/EmptyState'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { SectionLoader } from '@/components/common/SectionLoader'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/formatters'
import {
  useReplyToSupportTicket,
  useResolveSupportTicket,
  useSupportTicket,
} from '@/features/support/hooks/useSupportTickets'
import {
  MESSAGE_MAX,
  categoryLabel,
  priorityLabel,
  textareaClasses,
} from '@/features/support/supportLabels'
import { TicketStatusBadge } from './TicketStatusBadge'
import type { TicketDetail, TicketMessage } from '@/types/support-ticket.types'

function isNotFound(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status
  return status === 404
}

function MessageBubble({ message }: { message: TicketMessage }): React.ReactElement {
  const fromSupport = message.authorType === 'operator'
  return (
    <li className={cn('flex min-w-0', fromSupport ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'min-w-0 max-w-[92%] rounded-2xl border px-4 py-3 sm:max-w-[80%]',
          fromSupport
            ? 'rounded-tl-sm border-primary/20 bg-primary/10 dark:border-primary/30 dark:bg-primary/15'
            : 'rounded-tr-sm border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
        )}
      >
        <div className="mb-1 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span
            className={cn(
              'flex min-w-0 items-center gap-1 truncate text-xs font-semibold',
              fromSupport
                ? 'text-primary dark:text-primary-light'
                : 'text-gray-900 dark:text-gray-100'
            )}
          >
            {fromSupport && <LifeBuoy className="h-3.5 w-3.5 shrink-0" aria-hidden />}
            {fromSupport ? 'KAAMCO support' : message.authorName || 'You'}
          </span>
          {fromSupport && message.authorName && (
            <span className="truncate text-xs text-gray-500 dark:text-gray-400">
              {message.authorName}
            </span>
          )}
          {message.createdAt && (
            <time
              dateTime={message.createdAt}
              className="text-[11px] text-gray-500 dark:text-gray-400"
            >
              {formatDateTime(message.createdAt)}
            </time>
          )}
        </div>
        <p className="whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200">
          {message.body}
        </p>
      </div>
    </li>
  )
}

function ReplyBox({ ticket }: { ticket: TicketDetail }): React.ReactElement {
  const reply = useReplyToSupportTicket(ticket.id)
  const [body, setBody] = useState('')
  const trimmed = body.trim()
  const tooLong = trimmed.length > MESSAGE_MAX
  const isResolved = ticket.status === 'resolved'

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!trimmed || tooLong || reply.isPending) return
    reply.mutate(trimmed, { onSuccess: () => setBody('') })
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-2 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
    >
      <label
        htmlFor="ticket-reply"
        className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400"
      >
        {isResolved ? 'Reply to reopen' : 'Your reply'}
      </label>
      <textarea
        id="ticket-reply"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        maxLength={MESSAGE_MAX}
        aria-describedby="ticket-reply-help"
        placeholder={isResolved ? 'Still need help? Write a follow-up…' : 'Write a reply…'}
        className={textareaClasses}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p id="ticket-reply-help" className="min-w-0 flex-1 text-xs text-gray-500 dark:text-gray-400">
          {isResolved
            ? 'This ticket is resolved. Sending a reply reopens it for the support team.'
            : 'The KAAMCO support team is notified of your reply.'}
        </p>
        <Button
          type="submit"
          disabled={!trimmed || tooLong}
          loading={reply.isPending}
          loadingLabel="Sending reply"
        >
          <Send className="h-4 w-4" aria-hidden />
          <span className="ml-1.5">{isResolved ? 'Reply and reopen' : 'Send reply'}</span>
        </Button>
      </div>
    </form>
  )
}

function TicketHeader({ ticket }: { ticket: TicketDetail }): React.ReactElement {
  const resolve = useResolveSupportTicket(ticket.id)
  const isResolved = ticket.status === 'resolved'
  return (
    <header className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
              {ticket.reference}
            </span>
            <TicketStatusBadge status={ticket.status} />
          </div>
          <h1 className="break-words text-lg font-semibold text-gray-900 dark:text-gray-100">
            {ticket.subject}
          </h1>
        </div>
        {!isResolved && (
          <Button
            variant="secondary"
            onClick={() => resolve.mutate()}
            loading={resolve.isPending}
            loadingLabel="Marking as resolved"
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            <span className="ml-1.5">Mark as resolved</span>
          </Button>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:flex sm:flex-wrap sm:gap-x-6">
        <div className="min-w-0">
          <dt className="text-gray-500 dark:text-gray-400">Category</dt>
          <dd className="font-medium text-gray-900 dark:text-gray-100">
            {categoryLabel(ticket.category)}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-gray-500 dark:text-gray-400">Priority</dt>
          <dd className="font-medium text-gray-900 dark:text-gray-100">
            {priorityLabel(ticket.priority)}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-gray-500 dark:text-gray-400">Opened by</dt>
          <dd className="truncate font-medium text-gray-900 dark:text-gray-100" title={ticket.createdByEmail}>
            {ticket.createdByName}
          </dd>
        </div>
        {ticket.createdAt && (
          <div className="min-w-0">
            <dt className="text-gray-500 dark:text-gray-400">Opened</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {formatDateTime(ticket.createdAt)}
            </dd>
          </div>
        )}
        {isResolved && ticket.resolvedAt && (
          <div className="min-w-0">
            <dt className="text-gray-500 dark:text-gray-400">Resolved</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {formatDateTime(ticket.resolvedAt)}
            </dd>
          </div>
        )}
      </dl>
      {ticket.status === 'pending' && (
        <p className="rounded-md bg-primary/10 px-3 py-2 text-sm font-medium text-primary dark:text-primary-light">
          Support replied — reply below if you need anything else, or mark it as resolved.
        </p>
      )}
    </header>
  )
}

export function SupportTicketDetailPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const { data: ticket, isLoading, isError, error, refetch } = useSupportTicket(id)

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Help & support', to: '/support' },
        { label: ticket?.reference ?? 'Ticket' },
      ]}
      icon={LifeBuoy}
      title={ticket?.subject ?? 'Support ticket'}
      className="gap-0"
      hideHeader
    >
      <div className="mx-auto w-full max-w-3xl space-y-4">
        <Link
          to="/support"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          All tickets
        </Link>

        {isLoading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <SectionLoader variant="detail" label="Loading ticket" />
          </div>
        ) : isError && isNotFound(error) ? (
          <EmptyState
            icon={LifeBuoy}
            title="Ticket not found"
            description="It may have been raised by someone else, or the link is wrong."
            action={
              <Link
                to="/support"
                className="text-sm font-medium text-primary hover:underline dark:text-primary-light"
              >
                Back to your tickets
              </Link>
            }
          />
        ) : isError || !ticket ? (
          <QueryErrorState error={error} title="Couldn't load this ticket" onRetry={refetch} />
        ) : (
          <>
            <TicketHeader ticket={ticket} />
            <section aria-label="Conversation">
              <ol className="space-y-3">
                {ticket.messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </ol>
            </section>
            <ReplyBox ticket={ticket} />
          </>
        )}
      </div>
    </PageShell>
  )
}

SupportTicketDetailPage.displayName = 'SupportTicketDetailPage'
